import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { X } from 'lucide-react';

import { apiPost } from '../../../lib/api_v2';
import { compareBoardItems } from '../../../lib/boardItems';
import db from '../../../lib/db';
import { logError } from '../../../lib/logger';
import { cn } from '../../../lib/utils';
import { OPEN_WIDGET_BUILDER_EVENT } from '../../../lib/widgetGenerationNotifications';
import { canRemoveWidgetFromBoard, isUserCreatedWidget } from '../../../lib/widgetRemoval';

import NewWidgetsCollageImage from '../../../../assets/images/new-widgets-collage.png';
import SpinnerIcon from '../../../../assets/images/spinner.svg';
import WidgetOrderArrowIcon from '../../../../assets/images/widget-order-arrow.svg';

import { KEYCHAIN } from '../../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../../contexts/RouteWorkspaceContext';
import { useRemoveWidget } from '../../../hooks/useRemoveWidget';
import { useWidgetBoardSnapshot } from '../../../hooks/useWidgetBoardSnapshot';
import { isOptimisticBoardItemId } from '../../../hooks/useWidgetTemplateActions';
import { ModalPortal, useRetainedModalValue } from '../../modals/ModalPortal';
import RemoveWidgetConfirmation from '../../modals/RemoveWidgetConfirmation';
import Widget from '../../shared/Widget';
import WidgetGenerationBannerList from '../../shared/WidgetGenerationBannerList';

import './boardEditMode.css';

const EMPTY_BOARD_ITEMS = Object.freeze([]);
// Keep in sync with BoardItem::SORT_GAP. The server response is still the source
// of truth; this only lets the local optimistic order sort the same way.
const WIDGET_SORT_GAP = 1000;

const isReorderableBoardItem = (item) => Boolean(!item.pinned && item.boardable?.status === 'published');

export const canEditBoardWidget = (widget, interactive = true) => Boolean(interactive && isUserCreatedWidget(widget));

function widgetMoveSortUpdates(boardItems, boardItemId, direction) {
  if (direction !== 'up' && direction !== 'down') return null;

  const orderedWidgets = boardItems.filter((item) => !item.pinned).sort(compareBoardItems);
  const currentIndex = orderedWidgets.findIndex((item) => item.id === boardItemId);
  if (currentIndex === -1) return null;

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= orderedWidgets.length) return null;

  const nextOrderedWidgets = [...orderedWidgets];
  [nextOrderedWidgets[currentIndex], nextOrderedWidgets[targetIndex]] = [nextOrderedWidgets[targetIndex], nextOrderedWidgets[currentIndex]];

  return nextOrderedWidgets.map((item, index) => ({
    id: item.id,
    previousSortOrder: item.sort_order,
    optimisticSortOrder: WIDGET_SORT_GAP * (index + 1),
  }));
}

async function optimisticallyMoveWidgetBoardItem(workspaceId, boardItemId, direction) {
  return db.transaction('rw', db.board_items, async () => {
    const boardItems = await db.board_items.where('[workspace_id+boardable_type]').equals([workspaceId, 'widget']).toArray();
    const sortUpdates = widgetMoveSortUpdates(boardItems, boardItemId, direction);
    if (!sortUpdates) return null;

    for (const item of sortUpdates) {
      await db.board_items.update([workspaceId, item.id], { sort_order: item.optimisticSortOrder });
    }

    return sortUpdates;
  });
}

async function rollbackOptimisticWidgetBoardItem(workspaceId, sortUpdates) {
  await db.transaction('rw', db.board_items, async () => {
    for (const item of sortUpdates) {
      const current = await db.board_items.get([workspaceId, item.id]);
      if (current?.sort_order !== item.optimisticSortOrder) continue;

      await db.board_items.update([workspaceId, item.id], { sort_order: item.previousSortOrder });
    }
  });
}

// Edit-mode tiles shrink via `zoom`; pointer math happens in screen pixels, so
// inline translates on a zoomed tile divide by this to follow the cursor 1:1.
const EDIT_MODE_ZOOM = 0.4;

const WidgetOrderControls = React.memo(function WidgetOrderControls({ boardItemId, canMoveUp, canMoveDown, isMoving, onMove }) {
  const handleMoveUp = useCallback(() => onMove(boardItemId, 'up'), [boardItemId, onMove]);
  const handleMoveDown = useCallback(() => onMove(boardItemId, 'down'), [boardItemId, onMove]);
  const buttonClassName =
    'flex size-4 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:text-gray-500';

  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        className={buttonClassName}
        onClick={handleMoveUp}
        disabled={isMoving || !canMoveUp}
        aria-label="Move widget up"
        title="Move up"
      >
        <WidgetOrderArrowIcon />
      </button>
      <button
        type="button"
        className={buttonClassName}
        onClick={handleMoveDown}
        disabled={isMoving || !canMoveDown}
        aria-label="Move widget down"
        title="Move down"
      >
        <WidgetOrderArrowIcon className="rotate-180" />
      </button>
    </div>
  );
});

// A tile standing in for an install that is still in flight. It scrolls itself into view
// on mount so hitting Add always produces visible feedback, and it holds the real tile's
// footprint so the board does not reflow when the widget arrives to fill it.
const PendingBoardWidgetItem = React.memo(function PendingBoardWidgetItem({ title, visualOrder = 0 }) {
  const tileRef = useRef(null);

  useEffect(function revealPendingTile() {
    tileRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  return (
    <div ref={tileRef} style={{ order: visualOrder }} className="w-full">
      <article
        aria-busy="true"
        aria-label={`Adding ${title || 'widget'}`}
        className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-[20px] border-[length:var(--output-border-width)] border-white bg-gray-100 shadow-[0_9px_32px_rgba(0,0,0,0.08)]"
      >
        <div className="flex flex-col items-center gap-2">
          <SpinnerIcon className="size-5 animate-spin text-gray-400" />
          <span className="font-sans text-[13px]/[18px] text-gray-500">Adding {title || 'widget'}</span>
        </div>
      </article>
    </div>
  );
});

const BoardWidgetItem = React.memo(function BoardWidgetItem({
  boardItem,
  widget,
  canRemove,
  canEdit,
  canMoveUp,
  canMoveDown,
  onRemove,
  onEdit,
  onMove,
  orderControls,
  viewerUnavailableReason,
  widgetsEditing = false,
  dragHandlers = null,
  isDragged = false,
  dragTranslateY = 0,
  shiftTranslateY = 0,
  visualOrder = 0,
  wobbleDelayMs = 0,
}) {
  const boardItemId = boardItem.id;
  const handleRemove = useCallback((removedWidget) => onRemove?.(removedWidget, boardItemId), [onRemove, boardItemId]);
  const handleMove = useCallback((direction) => onMove?.(boardItemId, direction), [onMove, boardItemId]);
  // `zoom` (not transform scale) so the layout box shrinks with the tile and the
  // board's gap stays true between the small tiles. The card (not the wrapper)
  // loses pointer events so the wrapper itself can take hover and drag input.
  // `board-edit-tile` stays on in both modes so the zoom change animates on the
  // way in AND on the way out.
  const editWrapperClassName = isDragged
    ? 'board-edit-tile-dragging relative z-10 cursor-grabbing touch-none select-none [zoom:0.4] [&>article]:pointer-events-none [&>article]:shadow-[0px_16px_40px_0px_rgba(0,0,0,0.35)]'
    : 'board-edit-tile board-edit-wobble relative cursor-grab touch-none select-none hover:-translate-y-2 [zoom:0.4] [&>article]:pointer-events-none [&:hover>article]:shadow-[0px_16px_40px_0px_rgba(0,0,0,0.35)]';
  // iOS-style corner remove. Sized for the zoomed tile (56px renders ~22px).
  // stopPropagation on pointerdown keeps a tap on it from starting a drag.
  const cornerAction =
    widgetsEditing && canRemove ? (
      <button
        type="button"
        aria-label="Remove widget"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => handleRemove(widget)}
        className="board-edit-remove absolute -top-4 -left-4 z-20 flex size-14 cursor-pointer items-center justify-center rounded-full bg-black/80 text-white shadow-[0_4px_12px_rgba(0,0,0,0.25)] backdrop-blur-sm backdrop-saturate-[3] hover:bg-black/90"
      >
        <X size={28} strokeWidth={2.5} />
      </button>
    ) : null;
  // Inline translates happen inside the zoomed coordinate space, so screen-pixel
  // drag math divides by the zoom to track the cursor 1:1. `order` carries the
  // tile's visual position in every mode — DOM order stays fixed so reorders
  // never reload the widget iframes.
  const wrapperStyle = {
    order: visualOrder,
    ...(widgetsEditing
      ? {
          animationDelay: `${wobbleDelayMs}ms`,
          ...(isDragged ? { translate: `0 ${dragTranslateY / EDIT_MODE_ZOOM}px` } : null),
          ...(!isDragged && shiftTranslateY !== 0 ? { translate: `0 ${shiftTranslateY / EDIT_MODE_ZOOM}px` } : null),
        }
      : null),
  };
  return (
    <Widget
      data-board-item-id={boardItemId}
      wrapperClassName={widgetsEditing ? editWrapperClassName : 'board-edit-tile relative'}
      style={wrapperStyle}
      {...(widgetsEditing && dragHandlers ? dragHandlers : null)}
      cornerAction={cornerAction}
      lockEmbedHeight={widgetsEditing}
      widget={widget}
      canRemove={canRemove}
      canEdit={canEdit}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onRemove={handleRemove}
      onEdit={onEdit}
      onMoveRequest={handleMove}
      orderingControls={orderControls}
      viewerUnavailableReason={viewerUnavailableReason}
      hideActionsRow
    />
  );
});

const MissingBoardItem = React.memo(function MissingBoardItem({ boardItem, visualOrder = 0 }) {
  return (
    <article
      data-board-item-id={boardItem.id}
      style={{ order: visualOrder }}
      className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
    >
      <span className="text-sm text-gray-500 dark:text-neutral-400">Widget is not loaded.</span>
    </article>
  );
});

const BoardItem = React.memo(function BoardItem({
  item,
  canRemove,
  canEdit,
  canMoveUp,
  canMoveDown,
  showOrderControls,
  isMoving,
  onRemove,
  onEdit,
  onMove,
  viewerUnavailableReason,
  widgetsEditing = false,
  dragHandlers = null,
  isDragged = false,
  dragTranslateY = 0,
  shiftTranslateY = 0,
  visualOrder = 0,
  wobbleDelayMs = 0,
}) {
  if (!item.boardable) return <MissingBoardItem boardItem={item} visualOrder={visualOrder} />;

  const orderControls = showOrderControls ? (
    <WidgetOrderControls boardItemId={item.id} canMoveUp={canMoveUp} canMoveDown={canMoveDown} isMoving={isMoving} onMove={onMove} />
  ) : null;

  return (
    <BoardWidgetItem
      boardItem={item}
      widget={item.boardable}
      canRemove={canRemove}
      canEdit={canEdit}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onRemove={onRemove}
      onEdit={onEdit}
      onMove={onMove}
      orderControls={orderControls}
      viewerUnavailableReason={viewerUnavailableReason}
      widgetsEditing={widgetsEditing}
      dragHandlers={dragHandlers}
      isDragged={isDragged}
      dragTranslateY={dragTranslateY}
      shiftTranslateY={shiftTranslateY}
      visualOrder={visualOrder}
      wobbleDelayMs={wobbleDelayMs}
    />
  );
});

const BoardView = ({ interactive = true, viewerUnavailableReason, widgetsEditing = false }) => {
  const { workspaceId, profile: currentProfile, isDirectMessage = false } = useRouteWorkspace();
  const [movingBoardItemId, setMovingBoardItemId] = useState(null);
  const boardSnapshot = useWidgetBoardSnapshot(workspaceId, { requiresSnapshot: interactive && !isDirectMessage });
  const isLoading = boardSnapshot.status === 'loading';
  const items = boardSnapshot.status === 'ready' ? boardSnapshot.items : EMPTY_BOARD_ITEMS;
  const isAdmin = currentProfile?.role === 'admin';
  const reorderableBoardItemIds = useMemo(() => items.filter(isReorderableBoardItem).map((item) => item.id), [items]);
  // Tiles render in a FIXED DOM order (by id) and take their visual position
  // from flex `order` instead. Reordering an iframe's DOM node reloads it —
  // this way a reorder repaints without a single widget reload.
  const stableDomItems = useMemo(() => [...items].sort((a, b) => String(a.id).localeCompare(String(b.id))), [items]);
  const visualIndexById = useMemo(() => new Map(items.map((item, index) => [item.id, index])), [items]);
  // The board no longer hosts a builder of its own — both entry points hand off to the
  // studio (one host, one bootstrapped widget chat) via the same event the generation
  // banners already use.
  const openStudio = useCallback(
    (detail) => window.dispatchEvent(new CustomEvent(OPEN_WIDGET_BUILDER_EVENT, { detail: { workspaceId, ...detail } })),
    [workspaceId],
  );
  const handleOpenCreateModal = useCallback(() => openStudio({ mode: 'create' }), [openStudio]);
  const handleOpenEditModal = useCallback((widgetId) => openStudio({ widgetId }), [openStudio]);

  // Both actions remove the server-side board item; mirror that locally before the next sync.
  const handleAfterWidgetRemoval = useCallback(async ({ widget, context }) => {
    if (context?.boardItemId) await db.board_items.delete([widget.workspace_id, context.boardItemId]);
  }, []);
  const {
    session: removeWidgetSession,
    openConfirm: handleOpenRemoveConfirm,
    closeConfirm: handleCloseRemoveConfirm,
    confirmRemoval: handleConfirmRemoval,
  } = useRemoveWidget({ source: 'BoardView', onAfterRemove: handleAfterWidgetRemoval });
  const retainedRemoveWidgetSession = useRetainedModalValue(removeWidgetSession);

  useEffect(
    function closeTransientUiWhenBoardHides() {
      // Only the removal confirmation is the board's own now — the builder moved to the
      // studio, which outlives this component and manages its own dismissal.
      return handleCloseRemoveConfirm;
    },
    [handleCloseRemoveConfirm],
  );
  const handleOpenRemoveConfirmForBoardItem = useCallback(
    (widget, boardItemId) => handleOpenRemoveConfirm(widget, { boardItemId }),
    [handleOpenRemoveConfirm],
  );

  const handleMoveBoardItem = useCallback(
    async (boardItemId, direction) => {
      if (!workspaceId || movingBoardItemId) return;

      setMovingBoardItemId(boardItemId);
      let optimisticSortUpdates = null;
      let serverAcceptedMove = false;

      try {
        try {
          optimisticSortUpdates = await optimisticallyMoveWidgetBoardItem(workspaceId, boardItemId, direction);
        } catch (error) {
          logError('BoardView', 'Failed to optimistically move board item', { error, boardItemId, direction });
        }

        const response = await apiPost(`/board_items/${boardItemId}/move_${direction}`);
        serverAcceptedMove = response.ok;
        const data = await response.json();

        if (!response.ok || !data.ok) {
          serverAcceptedMove = false;
          throw new Error(data.errors ? JSON.stringify(data.errors) : 'board item move failed');
        }

        if (data.data?.board_items?.length) {
          try {
            await db.board_items.bulkPut(data.data.board_items);
          } catch (error) {
            logError('BoardView', 'Failed to store confirmed board item order', { error, boardItemId, direction });
            alert('Widget order changed, but this view could not load the confirmed layout. Reload to sync.');
          }
        }
      } catch (error) {
        if (!serverAcceptedMove && optimisticSortUpdates?.length) {
          try {
            await rollbackOptimisticWidgetBoardItem(workspaceId, optimisticSortUpdates);
          } catch (rollbackError) {
            logError('BoardView', 'Failed to rollback optimistic board item move', { error: rollbackError, boardItemId, direction });
          }
        }

        logError('BoardView', 'Failed to move board item', { error, boardItemId, direction });
        alert(
          serverAcceptedMove
            ? 'Widget order may have changed, but this view could not confirm the new layout. Reload to sync.'
            : 'Could not update widget order. Please try again.',
        );
      } finally {
        setMovingBoardItemId(null);
      }
    },
    [movingBoardItemId, workspaceId],
  );

  const isMoving = !!movingBoardItemId;

  // Pointer-drag reorder (edit mode). Geometry is snapshotted at grab time — the
  // other tiles hold still during a drag, so their centers stay valid. On drop
  // the displacement replays as sequential move_up/move_down calls, the only
  // reorder operations the API exposes.
  const [dragSession, setDragSession] = useState(null);
  const dragStateRef = useRef(null);
  // Optimistic visual order applied at drop time, ahead of the dexie writes, so
  // the reorder and the drag-translate release land in one commit.
  const [orderOverride, setOrderOverride] = useState(null);

  // Hand back to dexie once the confirmed order matches; the timer stops a
  // failed (rolled-back) move from pinning a stale layout.
  useEffect(() => {
    if (!orderOverride) return undefined;
    const matches = items.length > 0 && items.every((item, index) => orderOverride.get(item.id) === index);
    if (matches) {
      setOrderOverride(null);
      return undefined;
    }
    const timer = window.setTimeout(() => setOrderOverride(null), 2500);
    return () => window.clearTimeout(timer);
  }, [items, orderOverride]);

  useEffect(() => {
    if (widgetsEditing) return;
    dragStateRef.current = null;
    setDragSession(null);
    setOrderOverride(null);
  }, [widgetsEditing]);

  const handleTilePointerDown = useCallback(
    (event) => {
      if (event.button !== 0 || movingBoardItemId) return;
      const wrapper = event.currentTarget;
      const boardItemId = wrapper.dataset.boardItemId;
      if (!boardItemId || !reorderableBoardItemIds.includes(boardItemId)) return;

      // DOM order is stable (by id), not visual — sort the snapshot by on-screen
      // position so index math matches what the user sees.
      const tiles = Array.from(wrapper.parentElement?.querySelectorAll(':scope > [data-board-item-id]') || [])
        .filter((tile) => reorderableBoardItemIds.includes(tile.dataset.boardItemId))
        .map((tile) => {
          const rect = tile.getBoundingClientRect();
          return { id: tile.dataset.boardItemId, centerY: rect.top + rect.height / 2 };
        })
        .sort((tileA, tileB) => tileA.centerY - tileB.centerY);
      const draggedIndex = tiles.findIndex((tile) => tile.id === boardItemId);
      if (draggedIndex === -1) return;

      event.preventDefault();
      wrapper.setPointerCapture(event.pointerId);
      dragStateRef.current = {
        boardItemId,
        startY: event.clientY,
        tiles,
        draggedIndex,
        // How far displaced neighbors slide to open/close the gap: the dragged
        // tile's on-screen height plus the edit-mode gap (gap-1.5 = 6px).
        draggedExtent: wrapper.getBoundingClientRect().height + 6,
      };
      setDragSession({ boardItemId, translateY: 0 });
    },
    [movingBoardItemId, reorderableBoardItemIds],
  );

  const handleTilePointerMove = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    setDragSession({ boardItemId: dragState.boardItemId, translateY: event.clientY - dragState.startY });
  }, []);

  const handleTilePointerEnd = useCallback(
    async (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      dragStateRef.current = null;
      if (event.type === 'pointercancel') {
        setDragSession(null);
        return;
      }

      const { boardItemId, startY, tiles, draggedIndex } = dragState;
      const draggedCenterY = tiles[draggedIndex].centerY + (event.clientY - startY);
      const targetIndex = tiles.filter((tile) => tile.id !== boardItemId && tile.centerY < draggedCenterY).length;
      const steps = targetIndex - draggedIndex;
      if (steps === 0) {
        setDragSession(null);
        return;
      }

      // Land everything in ONE commit: the order override repositions every
      // tile in the same frame the drag translates clear, so displaced
      // neighbors don't move at all on release. `dropped` keeps the container
      // in its settling state (no translate transitions) while it happens.
      const currentIndex = items.findIndex((item) => item.id === boardItemId);
      if (currentIndex !== -1) {
        const nextItems = [...items];
        const [draggedItem] = nextItems.splice(currentIndex, 1);
        nextItems.splice(Math.min(Math.max(currentIndex + steps, 0), nextItems.length), 0, draggedItem);
        setOrderOverride(new Map(nextItems.map((item, index) => [item.id, index])));
      }
      setDragSession({ boardItemId, translateY: 0, dropped: true });
      window.setTimeout(() => {
        setDragSession((current) => (current?.dropped && current.boardItemId === boardItemId ? null : current));
      }, 200);

      // Saves happen per drop — Done only leaves the mode. The API exposes only
      // single-position moves, so a multi-slot drag replays as a sequence.
      const direction = steps > 0 ? 'down' : 'up';
      for (let i = 0; i < Math.abs(steps); i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await handleMoveBoardItem(boardItemId, direction);
      }
    },
    [handleMoveBoardItem, items],
  );

  const tileDragHandlers = useMemo(
    () => ({
      onPointerDown: handleTilePointerDown,
      onPointerMove: handleTilePointerMove,
      onPointerUp: handleTilePointerEnd,
      onPointerCancel: handleTilePointerEnd,
    }),
    [handleTilePointerDown, handleTilePointerMove, handleTilePointerEnd],
  );

  const renderBoardItem = useCallback(
    (item) => {
      const visualIndex = orderOverride?.get(item.id) ?? visualIndexById.get(item.id) ?? 0;
      // An install that has not come back yet.
      if (!item.boardable && isOptimisticBoardItemId(item.id)) {
        return <PendingBoardWidgetItem key={item.id} title={item.pending_title} visualOrder={visualIndex} />;
      }
      // A dropped session only drives the settling state; drag visuals stop.
      const activeDrag = dragSession && !dragSession.dropped ? dragSession : null;
      const reorderableIndex = reorderableBoardItemIds.indexOf(item.id);
      // Read-only mode (e.g. shared-channel sidebar) suppresses every edit/remove/reorder affordance.
      const canShowOrderControls = interactive && reorderableIndex !== -1;
      // While a neighbor is being dragged, this tile slides out of (or back into)
      // the way as the dragged tile's center crosses its snapshot center — the
      // same rule the drop uses to pick the target slot, so the preview and the
      // final order always agree.
      const dragGeometry = dragStateRef.current;
      let shiftTranslateY = 0;
      if (activeDrag && dragGeometry && activeDrag.boardItemId !== item.id) {
        const snapIndex = dragGeometry.tiles.findIndex((tile) => tile.id === item.id);
        if (snapIndex !== -1) {
          const draggedCenterY = dragGeometry.tiles[dragGeometry.draggedIndex].centerY + activeDrag.translateY;
          if (snapIndex > dragGeometry.draggedIndex && draggedCenterY > dragGeometry.tiles[snapIndex].centerY) {
            shiftTranslateY = -dragGeometry.draggedExtent;
          } else if (snapIndex < dragGeometry.draggedIndex && draggedCenterY < dragGeometry.tiles[snapIndex].centerY) {
            shiftTranslateY = dragGeometry.draggedExtent;
          }
        }
      }
      const canRemove =
        interactive &&
        canRemoveWidgetFromBoard(item.boardable, {
          currentProfileId: currentProfile?.id,
          isAdmin,
        });
      // Installed templates are never editable. Workspace members may continue only
      // user-created widget chats, regardless of who created the widget.
      const canEdit = canEditBoardWidget(item.boardable, interactive);
      const canMoveUp = canShowOrderControls && !isMoving && reorderableIndex > 0;
      const canMoveDown = canShowOrderControls && !isMoving && reorderableIndex < reorderableBoardItemIds.length - 1;

      return (
        <BoardItem
          key={item.id}
          item={item}
          canRemove={canRemove}
          canEdit={canEdit}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          showOrderControls={canShowOrderControls}
          isMoving={isMoving}
          onRemove={interactive ? handleOpenRemoveConfirmForBoardItem : undefined}
          onEdit={interactive ? handleOpenEditModal : undefined}
          onMove={interactive ? handleMoveBoardItem : undefined}
          viewerUnavailableReason={viewerUnavailableReason}
          widgetsEditing={widgetsEditing}
          dragHandlers={widgetsEditing && canShowOrderControls ? tileDragHandlers : null}
          isDragged={activeDrag?.boardItemId === item.id}
          dragTranslateY={activeDrag?.boardItemId === item.id ? activeDrag.translateY : 0}
          shiftTranslateY={shiftTranslateY}
          visualOrder={visualIndex}
          // Prime-stepped negative delays start each tile mid-cycle at a
          // different phase so the wobble reads organic, not synchronized.
          wobbleDelayMs={-((visualIndex * 97) % 280)}
        />
      );
    },
    [
      currentProfile?.id,
      handleMoveBoardItem,
      handleOpenRemoveConfirmForBoardItem,
      interactive,
      isAdmin,
      reorderableBoardItemIds,
      isMoving,
      orderOverride,
      handleOpenEditModal,
      viewerUnavailableReason,
      widgetsEditing,
      dragSession,
      tileDragHandlers,
      visualIndexById,
    ],
  );

  return (
    <div className="pointer-events-auto flex min-h-full w-full flex-col">
      {/* Outside the column below, not in it: that column animates its padding between
          modes, and a banner mounting mid-transition visibly grew from the edit-mode
          inset out to the normal one. Edit mode is about arranging what is already on
          the board, so the in-flight builds step out of the way while it is on. */}
      {/* Hidden, not unmounted: the list resolves its widget names from a live query, so
          tearing it down and rebuilding it made every row render its fallback name for a
          tick and then swap — the text visibly stuttered on the way back. */}
      {interactive ? (
        <div className={cn('shrink-0 pr-3 pb-3', widgetsEditing && 'hidden')}>
          <WidgetGenerationBannerList workspaceId={workspaceId} />
        </div>
      ) : null}
      {/* items-end always: the pane is exactly one tile wide in normal mode, so
          right-alignment only shows once edit mode shrinks the tiles — and a
          static alignment means nothing snaps sideways during the transition. */}
      <div
        className={cn(
          'flex flex-1 flex-col items-end pb-3 transition-[gap,padding] duration-300',
          // Centers a zoomed tile (328 × 0.4 ≈ 131px) under the Done pill. Measured from
          // the pane's right edge, so it holds at any pane width: the pill's center sits
          // 16px (nav px-4) + 292/2 in, and the tile's own half-width comes back off —
          // 16 + 146 − 65.6 ≈ 96. EditWidgetsButton is fixed at w-[292px] so this stays
          // a constant; change that width and this has to move with it.
          widgetsEditing ? 'gap-1.5 pr-[96px]' : 'gap-3 pr-3',
          dragSession?.dropped && 'board-edit-settling',
        )}
      >
        {isLoading ? (
          <div className="flex justify-center py-8" role="status" aria-label="Loading board">
            <SpinnerIcon className="size-4 animate-spin text-gray-500 dark:text-neutral-400" />
          </div>
        ) : null}
        {boardSnapshot.status === 'error' ? (
          <div className="flex w-full flex-col items-center gap-2 py-8 text-sm text-gray-500" role="alert">
            <span>Could not load widgets.</span>
            <button type="button" className="cursor-pointer text-blue-500 hover:text-blue-600" onClick={boardSnapshot.retry}>
              Try again
            </button>
          </div>
        ) : null}
        {boardSnapshot.status === 'ready' && items.length === 0 ? (
          <div className="relative aspect-square w-full overflow-hidden rounded-[12px] border-[0.5px] border-white bg-gray-50 shadow-[0px_9px_32px_0px_rgba(0,0,0,0.2)] dark:border-white/10 dark:bg-neutral-900">
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
              {/* 2x export rendered at 1x; overflow crops at the card edges by design */}
              <img
                src={NewWidgetsCollageImage}
                alt=""
                className="h-[275px] w-[430px] max-w-none -translate-x-[24px] -translate-y-[16px]"
                draggable="false"
              />
            </span>
            {interactive ? (
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className="group/cta absolute bottom-[30px] left-1/2 -translate-x-1/2 cursor-pointer"
              >
                <span
                  className="flex h-10 items-center justify-center rounded-full bg-[#0088ff] px-4 font-sans text-[14px] leading-[18px] font-medium whitespace-nowrap text-white shadow-[0px_9px_20px_rgba(0,0,0,0.2)] transition-transform duration-150 will-change-transform group-hover/cta:scale-[0.98]"
                  style={{ backgroundColor: 'color(display-p3 0 0.5333 1)' }}
                >
                  Add your first widget
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
        {stableDomItems.map(renderBoardItem)}
      </div>

      {interactive ? (
        <ModalPortal
          open={Boolean(removeWidgetSession)}
          onClose={handleCloseRemoveConfirm}
          size="mini"
          keychainId={KEYCHAIN.MODAL__REMOVE_WIDGET}
        >
          {retainedRemoveWidgetSession ? (
            <RemoveWidgetConfirmation
              action={retainedRemoveWidgetSession.action}
              onClose={handleCloseRemoveConfirm}
              onConfirm={handleConfirmRemoval}
            />
          ) : null}
        </ModalPortal>
      ) : null}
    </div>
  );
};

export default BoardView;
